import { makeClassName } from '@siteless/util';
import useCopy from '../util/useCopy';
import { oscarDatabase, tmdbConfig } from '../config/constants';
import withSuspense from './withSuspense';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useElementDimensions } from 'hooks';
import ArrowRightIcon from './ArrowRightIcon';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';
import { OneUserLetterboxdData } from '../resources/Letterboxd';
import LetterboxdIcon from './LetterboxdIcon';
import { IconAlertTriangle } from './IconAlertTriangle';
import LinkIcon from './LinkIcon';
import MapPinIcon from './MapPinIcon';
import { Tooltip } from './Tooltip';
import { addLinkProtocolIfMissing } from '../util/addLinkProtocolIfMissing';
import LetterboxdModal from './LetterboxdModal';
import { StarRating } from './StarRating';
import Action from './Action';
import { motion, useAnimationControls } from 'motion/react';
import { PopWrapper } from './PopWrapper';
import ParagraphIcon from './ParagraphIcon';
import { HeartIcon } from './HeartIcon';
import ParagraphShortIcon from './ParagraphShortIcon';
import ChevronLeftIcon from './ChevronLeftIcon';
import ChevronRightIcon from './ChevronRightIcon';
import CloseIcon from './CloseIcon';
import { TextOverflow } from './TextOverflow';
import React from 'react';
import { badges } from '../resources/Letterboxd/watchProgress';
import { tailwindVariables } from '../styles/tailwindVariables';
import { WatchBadge } from './WatchBadge';

export let LetterboxdBar = withSuspense(
  ({
    userId,
    letterboxdId,
    className,
    purpleBackgroundMode,
    hideBadges,
    condensed,
    errorMessage,
    errorElement,
  }: {
    userId: string;
    letterboxdId: string;
    className?: string;
    purpleBackgroundMode?: boolean;
    hideBadges?: boolean;
    condensed?: boolean;
    errorMessage?: string;
    errorElement?: React.ReactNode;
  }) => {
    let copy = useCopy();
    let currentUser = CurrentUserMustResource.use();
    let letterboxdResult = OneUserLetterboxdData.use(letterboxdId);
    let [showLetterboxdModal, setShowLetterboxdModal] = useState(false);
    let [selectedFilmId, setSelectedFilmId] = useState<string | null>(null);

    // Scroll anchor for when review panel expands
    let watchedTitleRef = useRef<HTMLDivElement>(null);
    let [reviewPanelElement, setReviewPanelElement] =
      useState<HTMLDivElement | null>(null);
    let reviewPanelDimensions = useElementDimensions(reviewPanelElement);
    let isLongReview = useMemo(
      () => reviewPanelDimensions.innerHeight > window.innerHeight * 0.8,
      [reviewPanelDimensions.innerHeight],
    );
    let [selectedFilmElement, setSelectedFilmElement] =
      useState<HTMLDivElement | null>(null);

    useEffect(() => {
      if (!selectedFilmElement) return;
      const timeout = setTimeout(() => {
        selectedFilmElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }, 20);
      return () => clearTimeout(timeout);
    }, [selectedFilmElement]);

    // Animation controls for the review container
    let reviewControls = useAnimationControls();
    let stiffClampTransition = {
      type: 'tween' as const,
      ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
      duration: 0.3,
    };

    let isCurrentUser = currentUser.id === userId;

    if (!letterboxdResult) return null;

    if (letterboxdResult.status === 'error') {
      if (errorElement) {
        return <>{errorElement}</>;
      }
      return (
        <div
          className={makeClassName(
            'flex items-center gap-2 rounded-lg py-2 px-4',
            purpleBackgroundMode
              ? 'bg-purple-800 text-purple-300'
              : 'bg-neutral-100 text-neutral-600',
            className,
          )}
        >
          <IconAlertTriangle className="h-4 w-4 flex-none" />
          <div className="text-sm">
            {errorMessage || copy['Letterboxd is unavailable right now']}
          </div>
        </div>
      );
    }

    let letterboxdData = letterboxdResult.data;

    let completedBadges = hideBadges
      ? []
      : Object.entries(badges).filter(([badgeKey, badge]) => {
          let { totalCount, watchedCount } = badge.getProgress({
            oscarFilmsStatus: letterboxdData.oscarFilmsStatus,
          });
          let isComplete = watchedCount === totalCount;
          return isComplete;
        });

    let links = [
      {
        key: 'letterboxd',
        url: `https://letterboxd.com/${letterboxdData.username}`,
        icon: <LetterboxdIcon className="inline-block h-4 w-4 flex-none" />,
        text: letterboxdData.username,
        noTruncate: true,
      },
      letterboxdData.website && {
        key: 'website',
        url: addLinkProtocolIfMissing(letterboxdData.website),
        icon: <LinkIcon className="inline-block h-4 w-4 flex-none" />,
        text: letterboxdData.website,
      },
      letterboxdData.location && {
        key: 'location',
        icon: <MapPinIcon className="inline-block h-4 w-4 flex-none" />,
        text: letterboxdData.location,
        isExternal: false,
      },
    ].filter(Boolean) as Array<{
      key: string;
      url?: string;
      icon: React.ReactNode;
      text: string;
      isExternal?: boolean;
      noTruncate?: boolean;
    }>;

    // sort films by .liked, then .review
    let films = Object.values(letterboxdData.oscarFilmsStatus)
      .filter((film) => film.watched)
      .sort((a, b) => {
        if (a.liked && !b.liked) return -1;
        if (!a.liked && b.liked) return 1;
        if (a.review && !b.review) return -1;
        if (!a.review && b.review) return 1;
        if (a.rating !== null && b.rating !== null) return b.rating - a.rating;
        return 0;
      })
      .filter((film) => {
        let filmData =
          oscarDatabase.films[film.id as keyof typeof oscarDatabase.films];
        return filmData !== undefined;
      })
      .map((film) => {
        let filmData =
          oscarDatabase.films[film.id as keyof typeof oscarDatabase.films];
        let posterPath = filmData.tmdb_data.tmdb_poster_path;
        let nominations = Object.values(oscarDatabase.nomineeCategories)
          .filter((category) =>
            Object.values(category.nominees).some(
              (nominee) => (nominee as { filmId: string }).filmId === film.id,
            ),
          )
          .map((category) => category.title);
        return {
          ...film,
          releaseDate: filmData.tmdb_data.tmdb_release_date,
          watchedDate: film.watchedDate || null,
          nominations,
          imageUrl: posterPath
            ? [
                tmdbConfig.base_url,
                tmdbConfig.poster_sizes.w154,
                posterPath,
              ].join('')
            : null,
        };
      });

    let selectedFilm = films.find((film) => film.id === selectedFilmId);
    let nextSelectedFilm =
      selectedFilm &&
      films.find((film) => films.indexOf(film) > films.indexOf(selectedFilm));
    let previousSelectedFilm =
      selectedFilm &&
      films
        .toReversed()
        .find((film) => films.indexOf(film) < films.indexOf(selectedFilm));

    let scrollToWatched = () =>
      watchedTitleRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

    let expandedFromCollapsedRef = useRef(false);

    useEffect(() => {
      if (selectedFilmId && !expandedFromCollapsedRef.current) {
        watchedTitleRef.current?.scrollIntoView({
          behavior: 'instant',
          block: 'start',
        });
      }
      expandedFromCollapsedRef.current = false;
    }, [selectedFilmId]);

    let handleFilmSelect = (filmId: string) => {
      if (selectedFilmId === filmId) {
        reviewControls
          .start({
            height: 0,
            opacity: 0,
            transition: stiffClampTransition,
          })
          .then(() => setSelectedFilmId(null));
      } else {
        if (selectedFilmId === null) {
          expandedFromCollapsedRef.current = true;
          setSelectedFilmId(filmId);
          reviewControls
            .start({
              height: 'auto',
              opacity: 1,
              transition: stiffClampTransition,
            })
            .then(scrollToWatched);
        } else {
          setSelectedFilmId(filmId);
        }
      }
    };

    let reviewNavControls = (
      <div className="flex flex-none items-center -my-1 -mr-1">
        <PopWrapper scaleTo={1.2}>
          <Action
            Icon={ChevronLeftIcon}
            disabled={!previousSelectedFilm}
            disablePadding
            className="p-1"
            onClick={() => {
              if (previousSelectedFilm) {
                handleFilmSelect(previousSelectedFilm.id);
              }
            }}
          />
        </PopWrapper>
        <PopWrapper scaleTo={1.2}>
          <Action
            Icon={ChevronRightIcon}
            disabled={!nextSelectedFilm}
            disablePadding
            className="p-1"
            onClick={() => {
              if (nextSelectedFilm) {
                handleFilmSelect(nextSelectedFilm.id);
              }
            }}
          />
        </PopWrapper>
        <PopWrapper scaleTo={1.2}>
          <Action
            Icon={CloseIcon}
            disablePadding
            className="p-1 !text-neutral-400"
            onClick={(e) => {
              e.stopPropagation();
              reviewControls
                .start({
                  height: 0,
                  opacity: 0,
                  transition: stiffClampTransition,
                })
                .then(() => setSelectedFilmId(null));
            }}
          />
        </PopWrapper>
      </div>
    );

    if (condensed) {
      return (
        <>
          <LetterboxdModal
            isOpen={showLetterboxdModal}
            onClose={() => {
              setShowLetterboxdModal(false);
            }}
          />
          <div
            className={makeClassName(
              'flex items-center justify-between rounded-lg bg-neutral-100 py-2 px-4',
              className,
            )}
          >
            <div className="flex items-center gap-2 text-sm">
              <LetterboxdIcon className="h-4 w-4 flex-none text-neutral-500" />
              <a
                href={`https://letterboxd.com/${letterboxdData.username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-neutral-700 underline"
              >
                {letterboxdData.username}
              </a>
              <span className="text-neutral-500">
                · {films.length} {copy.Watched.toLowerCase()}
              </span>
            </div>
            <span
              className="text-rose-bright cursor-pointer text-xs font-medium uppercase"
              onClick={() => {
                setShowLetterboxdModal(true);
              }}
            >
              {copy.Edit}
            </span>
          </div>
        </>
      );
    }

    return (
      <>
        <LetterboxdModal
          isOpen={showLetterboxdModal}
          onClose={() => {
            setShowLetterboxdModal(false);
          }}
        />
        <div
          className={makeClassName(
            className,
            purpleBackgroundMode ? '' : 'text-neutral-900',
          )}
        >
          <div className="mb-4 flex w-full min-w-0 items-center overflow-hidden">
            <div className="mr-1 flex flex-none items-center">
              {completedBadges.map(([badgeKey, badge], index) => {
                let pullIn = index > 0;
                return (
                  <div
                    key={badgeKey}
                    className={makeClassName(
                      'flex-none',
                      pullIn ? '-ml-3' : '',
                    )}
                  >
                    <Tooltip
                      text={copy.Badge + ': ' + badge.label}
                      tooltipSide="bottom"
                    >
                      <WatchBadge
                        renderArtwork={badge.renderBadgeArtwork}
                        label={badge.label}
                        size={28}
                        notCompletedFillColor={
                          tailwindVariables.theme.colors.purple[700]
                        }
                        notCompletedLineColor={
                          tailwindVariables.theme.colors.purple[400]
                        }
                        totalCount={1}
                        completedCount={1}
                        completedFillColor={badge.completedFillColor}
                        completedLineColor={badge.completedLineColor}
                      />
                    </Tooltip>
                  </div>
                );
              })}
            </div>
            <div
              className={makeClassName(
                'relative flex min-w-0 flex-1 items-center justify-between gap-2 border-neutral-100 px-1 text-xs font-medium',
                purpleBackgroundMode ? 'text-purple-600' : 'text-neutral-400',
              )}
            >
              {links.map((link) =>
                link.url ? (
                  <a
                    key={link.key}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={
                      link.noTruncate ? 'flex-shrink-0' : 'min-w-0 flex-1'
                    }
                  >
                    <div
                      className={makeClassName(
                        'flex items-center gap-1 underline',
                        !link.noTruncate && 'justify-end',
                      )}
                    >
                      {link.icon}
                      <span
                        className={makeClassName(
                          link.noTruncate ? '' : 'truncate',
                          purpleBackgroundMode
                            ? 'text-purple-400'
                            : 'text-neutral-500',
                        )}
                      >
                        {link.text}
                      </span>
                    </div>
                  </a>
                ) : (
                  <div
                    key={link.key}
                    className="flex min-w-0 flex-1 items-center justify-end gap-1"
                  >
                    {link.icon}
                    <TextOverflow
                      text={link.text}
                      className={makeClassName(
                        'truncate',
                        purpleBackgroundMode
                          ? 'text-purple-400'
                          : 'text-neutral-500',
                      )}
                    >
                      {link.text}
                    </TextOverflow>
                  </div>
                ),
              )}
              {links.length === 1 && !isCurrentUser && (
                <a
                  href={`https://letterboxd.com/${letterboxdData.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1"
                >
                  <span>{copy['See more on Letterboxd']}</span>
                  <ArrowRightIcon className="h-3 w-3" />
                </a>
              )}
              {isCurrentUser && (
                <span
                  className="text-rose-bright cursor-pointer uppercase"
                  onClick={() => {
                    setShowLetterboxdModal(true);
                  }}
                >
                  {copy.Edit}
                </span>
              )}
            </div>
          </div>

          <div
            className={makeClassName(
              'isolate rounded-lg pt-2',
              purpleBackgroundMode
                ? 'border-2 border-purple-800 bg-purple-900'
                : 'bg-neutral-100',
            )}
          >
            {films.length === 0 ? (
              <div className="px-4 pb-4 pt-2 text-center text-sm text-neutral-400">
                {copy['No Oscar nominees logged yet']}.
              </div>
            ) : (
              <>
                <div
                  ref={watchedTitleRef}
                  className="flex justify-between px-4 text-sm font-medium"
                >
                  {copy.Watched}
                </div>
                <div className="relative z-10 flex h-28 min-w-0 select-none items-end gap-2 overflow-x-auto overflow-y-visible px-4 pb-2">
                  {films.map((film) => (
                    <PopWrapper
                      scaleTo={1.075}
                      key={film.id}
                      forcePop={film.id === selectedFilmId ? 1.2 : false}
                      zIndex={film.id === selectedFilmId ? 10 : 0}
                    >
                      <div
                        ref={
                          film.id === selectedFilmId
                            ? setSelectedFilmElement
                            : undefined
                        }
                        className={makeClassName(
                          'relative cursor-pointer items-center transition-transform duration-200',
                        )}
                        onClick={() => {
                          handleFilmSelect(film.id);
                        }}
                      >
                        <div
                          className={makeClassName(
                            'absolute -top-2.5 -left-3 z-0 flex items-center',
                          )}
                        >
                          {film.liked && (
                            <div className="bg-neutral-0 -mt-/05 z-20 -mr-1 rounded-full border p-1 shadow-sm">
                              <HeartIcon className="text-rose-default h-4 w-4" />
                            </div>
                          )}
                          {film.formattedRating && (
                            <div className="bg-neutral-0 font-medium text-rose-dark z-10 mt-0.5 rounded-full border py-[1px] px-1.5 text-xs shadow-sm">
                              ★ {film.formattedRating}
                            </div>
                          )}
                        </div>
                        {film.review && (
                          <div
                            className={makeClassName(
                              'bg-neutral-0 absolute -bottom-1 right-1 rounded-full border px-1 text-xs text-neutral-600 transition-all duration-200 origin-center',
                              film.id === selectedFilmId
                                ? 'scale-0 opacity-0'
                                : 'scale-100 opacity-100',
                            )}
                          >
                            {film.review.length < 300 ? (
                              <ParagraphShortIcon className="h-4 w-4" />
                            ) : (
                              <ParagraphIcon className="h-4 w-4" />
                            )}
                          </div>
                        )}
                        <div className="mr-2 w-14 flex-none overflow-hidden rounded-md">
                          {film.imageUrl ? (
                            <img src={film.imageUrl} alt={film.title} />
                          ) : (
                            <div className="bg-neutral-0 flex h-full w-full items-center justify-center text-center text-[7px]">
                              {film.title}
                            </div>
                          )}
                        </div>
                      </div>
                    </PopWrapper>
                  ))}
                </div>
                <motion.div
                  className="relative z-0"
                  initial={{ height: 0, opacity: 0 }}
                  animate={reviewControls}
                  style={{ overflow: 'hidden' }}
                >
                  {selectedFilm && (
                    <div ref={setReviewPanelElement} className="p-1">
                      <div className="bg-neutral-0 rounded-lg px-4 py-3 text-neutral-900">
                        <div className="flex items-start justify-between gap-x-2">
                          <div className="min-w-0 font-medium">
                            {selectedFilm.title}
                          </div>
                          {reviewNavControls}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-neutral-500">
                          {selectedFilm.watchedDate ? (
                            <span>
                              Watched{' '}
                              {new Date(
                                selectedFilm.watchedDate,
                              ).toLocaleDateString()}
                            </span>
                          ) : (
                            selectedFilm.releaseDate && (
                              <span>
                                Released{' '}
                                {new Date(
                                  selectedFilm.releaseDate,
                                ).toLocaleDateString()}
                              </span>
                            )
                          )}
                          {(selectedFilm.watchedDate ||
                            selectedFilm.releaseDate) &&
                            selectedFilm.nominations.length > 0 && (
                              <span>•</span>
                            )}
                          {selectedFilm.nominations.length > 0 && (
                            <Tooltip
                              text={selectedFilm.nominations.join('\n')}
                              tooltipSide="bottom"
                            >
                              <span className="cursor-pointer underline decoration-dotted">
                                {selectedFilm.nominations.length} nomination
                                {selectedFilm.nominations.length > 1 ? 's' : ''}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedFilm.liked && (
                            <div className="text-rose-default text-xs font-medium">
                              <HeartIcon className="h-4 w-4" />
                            </div>
                          )}
                          {selectedFilm.rating !== null && (
                            <StarRating
                              rating={selectedFilm.rating}
                              starClassName="text-rose-dark"
                            />
                          )}
                        </div>
                        {selectedFilm.review && (
                          <>
                            <div
                              className="p-reset mt-3 text-left text-sm leading-6"
                              dangerouslySetInnerHTML={{
                                __html: selectedFilm.review || '',
                              }}
                            ></div>
                          </>
                        )}
                        {isLongReview && (
                          <div className="mt-3 flex justify-end">
                            {reviewNavControls}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              </>
            )}
          </div>
        </div>
      </>
    );
  },
  {
    ErrorBoundaryFallbackComponent: ({ className }: { className?: string }) => (
      <div
        className={makeClassName(
          'flex items-center gap-2 rounded-lg bg-neutral-100 py-2 px-4',
          className,
        )}
      >
        <IconAlertTriangle className="h-4 w-4 text-neutral-500" />
        <div className="text-sm text-neutral-600">
          Something went wrong fetching Letterboxd data
        </div>
      </div>
    ),
    FallbackComponent: ({ className }: { className?: string }) => (
      <div className={makeClassName('', className)}>
        <div className="mb-4 flex items-center justify-between px-1">
          <div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
          <div className="h-4 w-20 animate-pulse rounded bg-neutral-200" />
        </div>
        <div className="rounded-lg bg-neutral-100 pt-2">
          <div className="px-4 pb-1">
            <div className="h-4 w-16 animate-pulse rounded bg-neutral-200" />
          </div>
          <div className="flex h-28 items-end gap-2 px-4 pb-2">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-20 w-14 flex-none animate-pulse rounded-md bg-neutral-200"
              />
            ))}
          </div>
        </div>
      </div>
    ),
  },
);
